/**
 * Thin typed wrappers over the backend REST endpoints.
 *
 * Same-origin in production (nginx reverse-proxies ``/api/*`` to the
 * backend) and through Vite's dev proxy in development -- callers
 * just hit relative paths, no CORS dance.
 */

import type { AnyEnvelope } from '@/events';

const API_BASE = '/api/v1';

function apiKeyHeader(): Record<string, string> {
  const key = import.meta.env.VITE_API_KEY;
  return key ? { 'X-API-Key': key } : {};
}

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...apiKeyHeader(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown = undefined;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    throw new ApiError(`GET ${path} failed: ${res.status}`, res.status, body);
  }
  return (await res.json()) as T;
}

// ---------------- /building/state ----------------

export interface PodSummary {
  pod_id: string;
  name: string;
  risk_state: 'active' | 'watch' | 'reduced' | 'frozen';
  haircut_pct: number;
  risk_reason: string | null;
  risk_since: string | null;
  floor: FloorId;
  is_canary: boolean;
  capital: number;
  nav: number | null;
  daily_pnl: number | null;
  /** Economic-scale daily return (latest_session_nav vs session_open_nav,
   *  both with sandbox topups subtracted).  For heavily-topped-up pods
   *  this is amplified by the small denominator; prefer
   *  ``daily_return_broker_scale`` for the headline percentage. */
  daily_return: number | null;
  /** Raw broker NAV (totalAmountPortfolio from Tinkoff).  Optional --
   *  may be null on older backends or when the persisted
   *  ``raw_source_payload`` was unparseable. */
  broker_nav?: number | null;
  /** Broker-scale companion to ``daily_return``: same daily_pnl divided
   *  by the broker session-open NAV.  Single-digit honest % that
   *  matches what a real trader would see.  Optional for compatibility
   *  with older backends. */
  daily_return_broker_scale?: number | null;
  utility_today: number | null;
  utility_avg_10d: number | null;
  current_modifiers: Record<string, unknown>;
  last_action: Record<string, unknown> | null;
}

export type FloorId = 'canary_lab' | 'active_offices' | 'reduced_floor' | 'frozen_basement';

export interface FloorView {
  id: FloorId;
  label: string;
  pods: PodSummary[];
}

export interface BuildingCalendar {
  as_of: string;
  market_state: 'open' | 'closed' | 'holiday';
  next_session_open_at: string | null;
  next_session_close_at: string | null;
  next_daily_close_at: string;
  next_friday_celebration_at: string;
}

export interface BuildingState {
  as_of: string;
  floors: FloorView[];
  canary_pods: string[];
  policy_updater: {
    enabled: boolean;
    shadow_mode: boolean;
    cooldown_days: number;
  };
  calendar: BuildingCalendar;
  stats: {
    active_pods_count: number;
    canary_count: number;
  };
}

export const getBuildingState = (): Promise<BuildingState> => getJson('/building/state');

export const getCalendar = (): Promise<BuildingCalendar> => getJson('/system/calendar');

// ---------------- /dashboard/snapshot ----------------
//
// One-shot hydration payload.  Used on mount to seed the store with
// authoritative state -- positions, PM decisions, utility, agent
// phrases, nav history, etc. -- instead of trying to reconstruct
// them by replaying the persisted event-feed window (which can be
// short, stale, or missing infrequent events entirely).
//
// The TypeScript shape mirrors the JSON schema at
// ``PodPlatform/docs/dashboard_snapshot.schema.json``.  All
// per-pod fields are nullable because a freshly-created pod may not
// have emitted that event type yet.

export interface SnapshotPosition {
  ticker: string;
  qty: number | null;
  price: number | null;
  notional: number | null;
  weight: number | null;
  side: 'long' | 'short';
  figi: string | null;
  instrument_type: string | null;
}

export interface SnapshotPmDecision {
  lastUiSummary: string | null;
  lastRationale: string | null;
  confidence: number | null;
  targetGross: number | null;
  targetNet: number | null;
  intentsCount: number | null;
  decisionAt: string | null;
  topWeights: ReadonlyArray<{ ticker: string; weight: number }>;
}

export interface SnapshotUtility {
  total: number | null;
  components: Record<string, number> | null;
  version: string | null;
  recordedAt: string | null;
}

export interface SnapshotDisciplineFlag {
  code: string | null;
  severity: string | null;
  detail: Record<string, unknown> | null;
  raisedAt: string | null;
}

export interface SnapshotCenterbook {
  podScale: number | null;
  grossBefore: number | null;
  grossAfter: number | null;
  appliedAt: string | null;
}

export interface SnapshotCenterbookAllocation {
  capitalBefore: number | null;
  capitalAfter: number | null;
  deltaPct: number | null;
  reason: string | null;
  appliedAt: string | null;
}

export interface SnapshotPolicyAction {
  kind: 'no_change' | 'shadow_recommended' | 'applied' | 'rolled_back';
  triggerRule: string | null;
  paramChanged: string | null;
  valueAfter: unknown;
  auditId: number | null;
  at: string | null;
}

export interface SnapshotAlerts {
  errorCount: number;
  last: {
    ts: string;
    type: string;
    ticker: string | null;
    reason: string | null;
  } | null;
}

export interface SnapshotNavHistoryEntry {
  ts: string;
  nav: number;
  intradayReturn: number | null;
  intradayPnl: number | null;
}

export interface SnapshotAgentPhrase {
  text: string;
  kind: string | null;
  mood: string | null;
  ts: string;
}

export interface SnapshotAgentSignal {
  signal: number | null;
  confidence: number | null;
  topTickers: string[];
  latencyMs: number | null;
  ts: string;
}

export interface PodSnapshot {
  pod_id: string;
  nav: number | null;
  session_open_nav: number | null;
  intraday_pnl: number | null;
  intraday_return: number | null;
  /** Raw broker NAV (cash + positions, no sandbox-topup subtraction).
   *  ``null`` when broker data is unavailable. */
  broker_nav: number | null;
  /** Broker-scale session-open NAV (= ``session_open_nav + excess_topups``). */
  broker_open_nav: number | null;
  /** Companion to ``intraday_return`` computed on broker-NAV scale instead
   *  of economic-NAV scale.  Avoids the sandbox topup amplification: a
   *  real +2% broker day shows as +30%+ on economic scale for a
   *  15x-topped-up pod.  Frontend should PREFER this for headline %
   *  display; the economic ``intraday_return`` belongs in a tooltip /
   *  supplementary view. */
  broker_scale_intraday_return: number | null;
  gross_exposure: number | null;
  net_exposure: number | null;
  cash_balance: number | null;
  broker_source: string | null;
  positions_as_of: string | null;
  positions: SnapshotPosition[] | null;
  pm: SnapshotPmDecision | null;
  utility: SnapshotUtility | null;
  risk_state: 'active' | 'watch' | 'reduced' | 'frozen' | null;
  haircut_pct: number | null;
  risk_state_reason: string | null;
  risk_state_since: string | null;
  discipline_flag: SnapshotDisciplineFlag | null;
  centerbook: SnapshotCenterbook | null;
  centerbook_allocation: SnapshotCenterbookAllocation | null;
  policy_action: SnapshotPolicyAction | null;
  nav_history: SnapshotNavHistoryEntry[] | null;
  alerts: SnapshotAlerts | null;
  agent_phrases: Record<string, SnapshotAgentPhrase> | null;
  agent_signals: Record<string, SnapshotAgentSignal> | null;
  cycle_started: { ts: string; type: string; data: Record<string, unknown> } | null;
  cycle_completed: { ts: string; type: string; data: Record<string, unknown> } | null;
}

export interface DashboardSnapshot {
  as_of: string;
  building: BuildingState;
  pods: Record<string, PodSnapshot>;
  /** Present only when the backend couldn't reach Redis at request
   *  time.  The frontend falls back to its event-replay path. */
  degraded?: string;
}

export const getDashboardSnapshot = (): Promise<DashboardSnapshot> =>
  getJson('/dashboard/snapshot');

// ---------------- /events/replay ----------------

export interface ReplayResult {
  subject: string;
  count: number;
  events: AnyEnvelope[];
}

export interface ReplayParams {
  subject?: string;
  since?: string;
  limit?: number;
}

export function replayEvents({
  subject = 'system',
  since = '0-0',
  limit = 200,
}: ReplayParams = {}): Promise<ReplayResult> {
  const qs = new URLSearchParams({
    subject,
    since,
    limit: String(Math.min(Math.max(1, limit), 500)),
  });
  return getJson(`/events/replay?${qs.toString()}`);
}

export { ApiError };
